import React, { useState, useEffect } from 'react';
import { useAuth, useLanguage } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { 
  FileWarning, 
  AlertTriangle, 
  Home,
  Plus,
  ExternalLink,
  Phone,
  Mail,
  MapPin,
  Clock,
  CheckCircle,
  Shield,
  Search,
  BadgeCheck,
  FileCheck
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NYC_BOROUGHS = ['Bronx', 'Brooklyn', 'Manhattan', 'Queens', 'Staten Island'];

const DeedTheft = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [myAlerts, setMyAlerts] = useState([]);
  const [allAlerts, setAllAlerts] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filterBorough, setFilterBorough] = useState('all');
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Report form state
  const [reportForm, setReportForm] = useState({
    property_address: '',
    borough: '',
    bbl_number: '',
    description: '',
    contact_info: ''
  });

  useEffect(() => {
    fetchData();
    checkAdminStatus();
  }, [filterBorough]);

  const checkAdminStatus = async () => {
    try {
      const token = localStorage.getItem('polibot_token');
      if (token) {
        const response = await axios.get(`${API}/admin/check`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsAdmin(response.data.is_admin);
      }
    } catch (error) {
      setIsAdmin(false);
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('polibot_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [myAlertsRes, allAlertsRes, resourcesRes] = await Promise.all([
        axios.get(`${API}/deed-theft/my-alerts`, { headers }),
        axios.get(`${API}/deed-theft/alerts${filterBorough !== 'all' ? `?borough=${filterBorough}` : ''}`),
        axios.get(`${API}/deed-theft/resources`)
      ]);

      // Handle nested response structures
      setMyAlerts(myAlertsRes.data.alerts || myAlertsRes.data || []);
      setAllAlerts(allAlertsRes.data.alerts || allAlertsRes.data || []);
      setResources(resourcesRes.data.resources || resourcesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(language === 'es' ? 'Error al cargar datos' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleVerification = async (alertId) => {
    try {
      const token = localStorage.getItem('polibot_token');
      const response = await axios.put(`${API}/deed-theft/alerts/${alertId}/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.verification_status === 'community_verified' 
        ? (language === 'es' ? 'Marcado como verificado por la comunidad' : 'Marked as community verified')
        : (language === 'es' ? 'Marcado como oficial (ACRIS)' : 'Marked as official (ACRIS)'));
      fetchData();
    } catch (error) {
      toast.error(language === 'es' ? 'Error al actualizar' : 'Failed to update verification');
    }
  };

  const handleSubmitReport = async (e) => {
    e.preventDefault();
    if (!reportForm.property_address || !reportForm.borough) {
      toast.error(language === 'es' 
        ? 'Por favor complete la dirección y el distrito' 
        : 'Please fill in the address and borough');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('polibot_token');
      await axios.post(`${API}/deed-theft/report`, reportForm, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(language === 'es' 
        ? 'Reporte enviado exitosamente' 
        : 'Report submitted successfully');
      
      setReportDialogOpen(false);
      setReportForm({
        property_address: '',
        borough: '',
        bbl_number: '',
        description: '',
        contact_info: ''
      });
      fetchData();
    } catch (error) {
      toast.error(language === 'es' 
        ? 'Error al enviar el reporte' 
        : 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      reported: { label: language === 'es' ? 'Reportado' : language === 'ht' ? 'Rapòte' : 'Reported', className: 'bg-yellow-100 text-yellow-800' },
      investigating: { label: language === 'es' ? 'Investigando' : language === 'ht' ? 'Envestigasyon' : 'Investigating', className: 'bg-blue-100 text-blue-800' },
      resolved: { label: language === 'es' ? 'Resuelto' : language === 'ht' ? 'Rezoud' : 'Resolved', className: 'bg-green-100 text-green-800' },
      confirmed: { label: language === 'es' ? 'Confirmado' : language === 'ht' ? 'Konfime' : 'Confirmed', className: 'bg-red-100 text-red-800' },
      pending: { label: language === 'es' ? 'Pendiente' : language === 'ht' ? 'Annatant' : 'Pending', className: 'bg-gray-100 text-gray-800' }
    };
    const config = statusConfig[status] || statusConfig.reported;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const getVerificationBadge = (verificationStatus) => {
    if (verificationStatus === 'community_verified') {
      return (
        <Badge className="bg-green-100 text-green-800 flex items-center gap-1">
          <BadgeCheck className="h-3 w-3" />
          {language === 'es' ? 'Verificado por Comunidad' : language === 'ht' ? 'Verifye pa Kominote' : 'Community Verified'}
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="flex items-center gap-1">
        <FileCheck className="h-3 w-3" />
        {language === 'es' ? 'Oficial (ACRIS)' : language === 'ht' ? 'Ofisyèl (ACRIS)' : 'Official (ACRIS)'}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="deed-theft-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="deed-theft-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2">
            <FileWarning className="h-8 w-8 text-accent" />
            {language === 'es' ? 'Alertas de Robo de Escrituras' : 'Deed Theft Alerts'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {language === 'es' 
              ? 'Protege tu propiedad y reporta actividades sospechosas'
              : 'Protect your property and report suspicious activity'}
          </p>
        </div>
        <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-accent hover:bg-accent/90" data-testid="report-deed-theft-btn">
              <Plus className="h-4 w-4 mr-2" />
              {language === 'es' ? 'Reportar Actividad Sospechosa' : 'Report Suspicious Activity'}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-accent" />
                {language === 'es' ? 'Reportar Posible Robo de Escritura' : 'Report Potential Deed Theft'}
              </DialogTitle>
              <DialogDescription>
                {language === 'es' 
                  ? 'Complete el formulario para reportar actividad sospechosa relacionada con su propiedad.'
                  : 'Fill out the form to report suspicious activity related to your property.'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmitReport}>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="property_address">
                    {language === 'es' ? 'Dirección de la Propiedad *' : 'Property Address *'}
                  </Label>
                  <Input
                    id="property_address"
                    value={reportForm.property_address}
                    onChange={(e) => setReportForm({...reportForm, property_address: e.target.value})}
                    placeholder="123 Main St, Apt 4B"
                    data-testid="property-address-input"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="borough">
                    {language === 'es' ? 'Distrito *' : 'Borough *'}
                  </Label>
                  <Select 
                    value={reportForm.borough} 
                    onValueChange={(val) => setReportForm({...reportForm, borough: val})}
                  >
                    <SelectTrigger data-testid="borough-select">
                      <SelectValue placeholder={language === 'es' ? 'Seleccionar distrito' : 'Select borough'} />
                    </SelectTrigger>
                    <SelectContent>
                      {NYC_BOROUGHS.map(b => (
                        <SelectItem key={b} value={b}>{b}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="bbl_number">
                    {language === 'es' ? 'Número BBL (opcional)' : 'BBL Number (optional)'}
                  </Label>
                  <Input
                    id="bbl_number"
                    value={reportForm.bbl_number}
                    onChange={(e) => setReportForm({...reportForm, bbl_number: e.target.value})}
                    placeholder="1-00001-0001"
                    data-testid="bbl-input"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="description">
                    {language === 'es' ? 'Descripción' : 'Description'}
                  </Label>
                  <Textarea
                    id="description"
                    value={reportForm.description}
                    onChange={(e) => setReportForm({...reportForm, description: e.target.value})}
                    placeholder={language === 'es' 
                      ? 'Describa la actividad sospechosa...'
                      : 'Describe the suspicious activity...'}
                    rows={3}
                    data-testid="description-input"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="contact_info">
                    {language === 'es' ? 'Información de Contacto' : 'Contact Information'}
                  </Label>
                  <Input
                    id="contact_info"
                    value={reportForm.contact_info}
                    onChange={(e) => setReportForm({...reportForm, contact_info: e.target.value})}
                    placeholder={language === 'es' ? 'Teléfono o correo' : 'Phone or email'}
                    data-testid="contact-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setReportDialogOpen(false)}>
                  {language === 'es' ? 'Cancelar' : 'Cancel'}
                </Button>
                <Button type="submit" disabled={submitting} className="bg-accent hover:bg-accent/90" data-testid="submit-report-btn">
                  {submitting 
                    ? (language === 'es' ? 'Enviando...' : 'Submitting...') 
                    : (language === 'es' ? 'Enviar Reporte' : 'Submit Report')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-accent">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {language === 'es' ? 'Mis Reportes' : 'My Reports'}
                </p>
                <p className="text-3xl font-bold mt-1">{myAlerts.length}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center">
                <Home className="h-6 w-6 text-accent" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-warning">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {language === 'es' ? 'Alertas Activas NYC' : 'Active NYC Alerts'}
                </p>
                <p className="text-3xl font-bold mt-1">{allAlerts.filter(a => a.status !== 'resolved').length}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-warning/10 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-warning" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-success">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {language === 'es' ? 'Recursos Disponibles' : 'Resources Available'}
                </p>
                <p className="text-3xl font-bold mt-1">{resources.length}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-success/10 flex items-center justify-center">
                <Shield className="h-6 w-6 text-success" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="my-alerts" className="space-y-4">
        <TabsList>
          <TabsTrigger value="my-alerts" data-testid="tab-my-alerts">
            {language === 'es' ? 'Mis Alertas' : 'My Alerts'}
          </TabsTrigger>
          <TabsTrigger value="all-alerts" data-testid="tab-all-alerts">
            {language === 'es' ? 'Alertas NYC' : 'NYC Alerts'}
          </TabsTrigger>
          <TabsTrigger value="resources" data-testid="tab-resources">
            {language === 'es' ? 'Recursos' : 'Resources'}
          </TabsTrigger>
        </TabsList>

        {/* My Alerts Tab */}
        <TabsContent value="my-alerts">
          <Card>
            <CardHeader>
              <CardTitle>{language === 'es' ? 'Mis Reportes de Propiedad' : language === 'ht' ? 'Rapò Pwopriyete Mwen' : 'My Property Reports'}</CardTitle>
              <CardDescription>
                {language === 'es' 
                  ? 'Seguimiento de reportes que has enviado'
                  : language === 'ht' ? 'Swiv rapò ou voye yo' : 'Track reports you have submitted'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {myAlerts.length > 0 ? (
                <div className="space-y-4">
                  {myAlerts.map((alert, index) => (
                    <div 
                      key={alert.id} 
                      className={`flex items-start gap-4 p-4 rounded-lg border ${
                        alert.verification_status === 'community_verified' 
                          ? 'border-green-200 bg-green-50/30' 
                          : 'border-border/60'
                      }`}
                      data-testid={`my-alert-${index}`}
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        alert.verification_status === 'community_verified' ? 'bg-green-100' : 'bg-accent/10'
                      }`}>
                        {alert.verification_status === 'community_verified' 
                          ? <BadgeCheck className="h-5 w-5 text-green-600" />
                          : <Home className="h-5 w-5 text-accent" />
                        }
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div>
                            <h4 className="font-medium">{alert.property_address}</h4>
                            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                              <MapPin className="h-3 w-3" />
                              {alert.borough}
                            </p>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            {getVerificationBadge(alert.verification_status)}
                            {getStatusBadge(alert.status)}
                          </div>
                        </div>
                        {alert.description && (
                          <p className="text-sm text-muted-foreground mt-2">{alert.description}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(alert.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Home className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>{language === 'es' ? 'No tienes reportes' : language === 'ht' ? 'Ou pa gen rapò' : 'You have no reports'}</p>
                  <Button 
                    variant="link" 
                    onClick={() => setReportDialogOpen(true)}
                    className="mt-2"
                  >
                    {language === 'es' ? 'Crear tu primer reporte' : language === 'ht' ? 'Kreye premye rapò ou' : 'Create your first report'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* All NYC Alerts Tab */}
        <TabsContent value="all-alerts">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle>{language === 'es' ? 'Alertas en NYC' : 'NYC-Wide Alerts'}</CardTitle>
                  <CardDescription>
                    {language === 'es' 
                      ? 'Reportes de actividad sospechosa en toda la ciudad'
                      : 'Reports of suspicious activity across the city'}
                  </CardDescription>
                </div>
                <Select value={filterBorough} onValueChange={setFilterBorough}>
                  <SelectTrigger className="w-[180px]" data-testid="filter-borough">
                    <MapPin className="h-4 w-4 mr-2" />
                    <SelectValue placeholder={language === 'es' ? 'Filtrar' : 'Filter'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{language === 'es' ? 'Todos los Distritos' : 'All Boroughs'}</SelectItem>
                    {NYC_BOROUGHS.map(b => (
                      <SelectItem key={b} value={b}>{b}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {allAlerts.length > 0 ? (
                <div className="space-y-4">
                  {allAlerts.map((alert, index) => (
                    <div 
                      key={alert.id} 
                      className={`flex items-start gap-4 p-4 rounded-lg border transition-colors ${
                        alert.verification_status === 'community_verified' 
                          ? 'border-green-200 bg-green-50/30' 
                          : 'border-border/60 hover:border-primary/20'
                      }`}
                      data-testid={`nyc-alert-${index}`}
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        alert.verification_status === 'community_verified' ? 'bg-green-100' :
                        alert.status === 'confirmed' ? 'bg-red-100' : 'bg-yellow-100'
                      }`}>
                        {alert.verification_status === 'community_verified' ? (
                          <BadgeCheck className="h-5 w-5 text-green-600" />
                        ) : (
                          <AlertTriangle className={`h-5 w-5 ${
                            alert.status === 'confirmed' ? 'text-red-600' : 'text-yellow-600'
                          }`} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div>
                            <h4 className="font-medium">{alert.property_address}</h4>
                            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                              <MapPin className="h-3 w-3" />
                              {alert.borough}
                            </p>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            {getVerificationBadge(alert.verification_status)}
                            {getStatusBadge(alert.status)}
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(alert.created_at).toLocaleDateString()}
                        </p>
                        
                        {/* Admin Controls */}
                        {isAdmin && (
                          <div className="mt-3 pt-3 border-t border-border/40">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleToggleVerification(alert.id)}
                              className={alert.verification_status === 'community_verified' 
                                ? 'border-green-500 text-green-600 hover:bg-green-50' 
                                : ''}
                              data-testid={`toggle-verify-${index}`}
                            >
                              <BadgeCheck className="h-4 w-4 mr-2" />
                              {alert.verification_status === 'community_verified' 
                                ? (language === 'es' ? 'Quitar Verificación' : 'Remove Verification')
                                : (language === 'es' ? 'Marcar como Verificado' : 'Mark as Verified')}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>{language === 'es' ? 'No hay alertas activas' : 'No active alerts'}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Resources Tab */}
        <TabsContent value="resources">
          <div className="grid gap-4 md:grid-cols-2">
            {resources.map((resource, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow" data-testid={`resource-${index}`}>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-2">{resource.title || resource.name}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{resource.description}</p>
                  <div className="space-y-2">
                    {resource.phone && (
                      <a 
                        href={`tel:${resource.phone}`} 
                        className="flex items-center gap-2 text-sm text-primary hover:underline"
                      >
                        <Phone className="h-4 w-4" />
                        {resource.phone}
                      </a>
                    )}
                    {resource.email && (
                      <a 
                        href={`mailto:${resource.email}`} 
                        className="flex items-center gap-2 text-sm text-primary hover:underline"
                      >
                        <Mail className="h-4 w-4" />
                        {resource.email}
                      </a>
                    )}
                    {(resource.website || resource.url) && (
                      <a 
                        href={resource.website || resource.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-primary hover:underline"
                      >
                        <ExternalLink className="h-4 w-4" />
                        {language === 'es' ? 'Visitar sitio web' : 'Visit website'}
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DeedTheft;
